import * as React from 'react';
import { Link } from 'react-router-dom';
import type {
  GrowthFeedback,
  GrowthPlan,
  GrowthResponse,
} from '@shared/api-client/growth';
import { ArrowRight, Compass, SlidersHorizontal } from 'lucide-react';
import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Opportunity } from './opportunity';
import { PlanForm } from './plan-form';
import { cn } from '@/lib/utils';

export const OpportunitiesView = ({
  data,
  projectId,
  projectName,
  canManage,
  loading,
  error,
  onRetry,
  onPlan,
  onFeedback,
  savingPlan = false,
  savingId,
  planError,
  feedbackError,
  needsProfile = false,
}: {
  data?: GrowthResponse;
  projectId: string;
  projectName: string;
  canManage: boolean;
  loading?: boolean;
  error?: string;
  onRetry: () => void;
  onPlan: (plan: GrowthPlan) => Promise<void>;
  onFeedback: (id: string, feedback: GrowthFeedback) => void;
  savingPlan?: boolean;
  savingId?: string;
  planError?: string;
  feedbackError?: { id: string; message: string };
  needsProfile?: boolean;
}) => {
  const [settings, setSettings] = React.useState(false);
  const [tab, setTab] = React.useState<'active' | 'history'>('active');
  const run = data?.run;
  const items =
    data?.opportunities.filter((item) =>
      tab === 'history'
        ? ['done', 'dismissed'].includes(item.status)
        : !['done', 'dismissed'].includes(item.status),
    ) ?? [];
  const status = needsProfile
    ? 'Add your product context to start research'
    : !data?.plan.enabled
      ? 'Research paused'
      : !run
        ? 'Preparing your first research'
        : run.status === 'processing' || run.status === 'queued'
          ? 'Research in progress'
          : run.status === 'failed'
            ? 'Research needs attention'
            : `Last checked ${new Date(run.completedAt!).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`;
  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-ink">
            Opportunities
          </h1>
          <p className="mt-2 max-w-xl text-sm leading-relaxed text-ink-muted">
            What’s worth trying next for {projectName}, with the evidence to
            decide.
          </p>
        </div>
        <Button
          variant="secondary"
          disabled={!data}
          aria-expanded={settings}
          onClick={() => setSettings(!settings)}
        >
          <SlidersHorizontal />
          Research focus
        </Button>
      </header>
      {error && (
        <Alert tone="error">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <span>{error}</span>
            <Button size="sm" variant="secondary" onClick={onRetry}>
              Try again
            </Button>
          </div>
        </Alert>
      )}
      {loading && (
        <div className="space-y-4" aria-label="Loading opportunities">
          <Skeleton className="h-24 rounded-xl" />
          <Skeleton className="h-96 rounded-xl" />
        </div>
      )}
      {data && (
        <>
          {needsProfile && (
            <Alert>
              <Link
                className="text-accent underline"
                to={`/projects/${projectId}/setup?edit=1`}
              >
                Add a website or product description
              </Link>{' '}
              so GainSignal can research the right market.
            </Alert>
          )}
          {settings && (
            <PlanForm
              key={`${projectId}:${data.plan.goal}:${data.plan.competitors.join(',')}:${data.plan.topics.join(',')}:${data.plan.enabled}`}
              plan={data.plan}
              canManage={canManage}
              saving={savingPlan}
              error={planError}
              onCancel={() => setSettings(false)}
              onSave={(plan) => {
                void onPlan(plan)
                  .then(() => setSettings(false))
                  .catch(() => undefined);
              }}
            />
          )}
          <div className="flex flex-col justify-between gap-4 rounded-xl border border-border px-5 py-4 sm:flex-row sm:items-start">
            <div className="min-w-0">
              <p className="flex items-center gap-2 text-sm font-medium text-ink">
                <span
                  className={cn(
                    'size-2 shrink-0 rounded-full',
                    data.plan.enabled && run?.status !== 'failed'
                      ? 'bg-positive'
                      : 'bg-ink-muted',
                  )}
                />
                {status}
              </p>
              <p className="mt-2 max-w-2xl text-sm leading-relaxed text-ink-muted">
                {data.plan.goal}
              </p>
              <p className="mt-2 text-xs text-ink-muted">
                {data.plan.competitors.length
                  ? `${data.plan.competitors.length} competitor ${data.plan.competitors.length === 1 ? 'page' : 'pages'} · `
                  : 'Discovering relevant competitors · '}
                {data.plan.topics.length
                  ? `${data.plan.topics.length} topics · `
                  : ''}
                Daily checks · Up to 3 new opportunities a week
              </p>
            </div>
            <Link
              className="inline-flex shrink-0 items-center gap-1 text-sm text-accent"
              to={`/projects/${projectId}/reports`}
            >
              View your analytics
              <ArrowRight className="size-4" />
            </Link>
          </div>
          {run?.error && <Alert tone="error">{run.error}</Alert>}
          {!!run?.warnings.length && (
            <details className="rounded-lg border border-border px-4 py-3 text-sm text-ink-muted">
              <summary className="cursor-pointer text-ink">
                Research coverage · {run.warnings.length}{' '}
                {run.warnings.length === 1 ? 'limitation' : 'limitations'}
              </summary>
              <ul className="mt-3 list-disc space-y-2 pl-5">
                {run.warnings.map((warning, i) => (
                  <li key={i}>{warning}</li>
                ))}
              </ul>
            </details>
          )}
          <div
            className="flex gap-5 border-b border-border"
            aria-label="Opportunity filters"
          >
            {(['active', 'history'] as const).map((value) => (
              <button
                key={value}
                type="button"
                aria-pressed={tab === value}
                className={cn(
                  'border-b-2 px-1 pb-3 text-sm font-medium',
                  tab === value
                    ? 'border-accent text-ink'
                    : 'border-transparent text-ink-muted hover:text-ink',
                )}
                onClick={() => setTab(value)}
              >
                {value === 'active' ? 'For you' : 'Reviewed & dismissed'}
              </button>
            ))}
          </div>
          {!items.length ? (
            <div className="rounded-xl border border-border bg-surface px-6 py-12 text-center">
              <Compass className="mx-auto size-7 text-ink-muted" />
              <h2 className="mt-4 font-semibold text-ink">
                {tab === 'history'
                  ? 'Your decisions will stay here'
                  : !data.plan.enabled
                    ? 'Research is paused'
                    : run?.status === 'completed'
                      ? 'Nothing strong enough to recommend yet'
                      : run?.status === 'failed'
                        ? 'Your research hasn’t finished'
                        : 'Getting to know your market'}
              </h2>
              <p className="mx-auto mt-2 max-w-lg text-sm leading-relaxed text-ink-muted">
                {tab === 'history'
                  ? 'Review or dismiss an opportunity to build a history of what you’ve considered.'
                  : !data.plan.enabled
                    ? 'Resume from Research focus when you’re ready. Your opportunities and notes are saved.'
                    : run?.status === 'completed'
                      ? 'GainSignal will keep checking. Suggestions appear when there is relevant evidence and a practical next step.'
                      : run?.status === 'failed'
                        ? 'No conclusions were drawn from the failed check. Earlier opportunities remain available, and research will retry automatically.'
                        : 'GainSignal uses your website and product profile to research competitors and customer problems. The first check starts automatically, usually within 15 minutes. You can refine its focus above.'}
              </p>
            </div>
          ) : (
            <div className="space-y-5">
              {items.map((item) => (
                <Opportunity
                  key={`${item.id}:${item.status}:${item.reviewAt}:${item.note}:${item.useful}`}
                  item={item}
                  canManage={canManage}
                  saving={savingId === item.id}
                  error={
                    feedbackError?.id === item.id
                      ? feedbackError.message
                      : undefined
                  }
                  onSave={onFeedback}
                />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};
